import React, { useMemo, useState } from "react";
import { cn } from "../../lib/utils";

const LEVEL_DOT = {
  critical: "bg-red-500", high: "bg-orange-500", medium: "bg-amber-500", low: "bg-emerald-500",
};

function Matrix({ title, risks, likelihoodKey, impactKey, levelKey, onSelect, testIdPrefix }) {
  // rows = likelihood 5 (top) -> 1 (bottom); cols = impact 1 (left) -> 5 (right)
  const cells = useMemo(() => {
    const grid = Array.from({ length: 5 }, () => Array.from({ length: 5 }, () => []));
    risks.forEach((r) => {
      const l = Math.min(5, Math.max(1, r[likelihoodKey] || 1));
      const i = Math.min(5, Math.max(1, r[impactKey] || 1));
      grid[5 - l][i - 1].push(r);
    });
    return grid;
  }, [risks, likelihoodKey, impactKey]);

  const bandForCount = (count) => {
    if (count === 0) return "bg-muted/30";
    if (count === 1) return "bg-amber-500/15";
    return "bg-red-500/20";
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testIdPrefix}>
      <div className="text-[13px] font-semibold mb-3">{title}</div>
      <div className="flex gap-2">
        <div className="flex flex-col justify-between text-[10px] text-muted-foreground py-1 w-10 text-right pr-1">
          <span>High</span><span className="rotate-180" style={{ writingMode: "vertical-rl" }}>Likelihood</span><span>Low</span>
        </div>
        <div className="flex-1">
          <div className="grid grid-cols-5 gap-1">
            {cells.flatMap((row, ri) => row.map((riskList, ci) => (
              <div
                key={`${ri}-${ci}`}
                className={cn("aspect-square rounded flex flex-wrap items-center justify-center gap-0.5 p-0.5 overflow-hidden", bandForCount(riskList.length))}
                data-testid={`${testIdPrefix}-cell-${ri}-${ci}`}
              >
                {riskList.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    title={`${r.code} — ${r.title}`}
                    onClick={() => onSelect?.(r)}
                    className={cn("w-4 h-4 rounded-full text-[7px] text-white font-bold flex items-center justify-center shrink-0 hover:scale-125 transition-transform", LEVEL_DOT[r[levelKey]] || "bg-muted-foreground")}
                    data-testid={`${testIdPrefix}-dot-${r.id}`}
                  >
                    {r.code.replace(/^R-0*|^VR-/, "").slice(0, 2)}
                  </button>
                ))}
              </div>
            )))}
          </div>
          <div className="text-center text-[10px] text-muted-foreground mt-1">Impact (Low → High)</div>
        </div>
      </div>
    </div>
  );
}

export default function RiskMatrixPair({ risks, onSelect }) {
  return (
    <div className="space-y-2">
      <div className="grid md:grid-cols-2 gap-3">
        <Matrix
          title="Current (inherent) risk"
          risks={risks}
          likelihoodKey="inherent_likelihood"
          impactKey="inherent_impact"
          levelKey="inherent_level"
          onSelect={onSelect}
          testIdPrefix="risk-matrix-inherent"
        />
        <Matrix
          title="Residual risk"
          risks={risks}
          likelihoodKey="residual_likelihood"
          impactKey="residual_impact"
          levelKey="residual_level"
          onSelect={onSelect}
          testIdPrefix="risk-matrix-residual"
        />
      </div>
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="risk-matrix-legend">
        <span className="w-3.5 h-3.5 rounded-full bg-muted-foreground inline-block" /> Each dot = one risk (hover for code & title, click to open) · darker cells hold more than one risk
      </div>
    </div>
  );
}
